'use client';

import { useAccountBalance } from '@svenskaspelab/nova-core/nextjs/auth';
import type { WalletType } from '@svenskaspelab/poolbetting/schema/frameworks';
import type { ActivatedDrawId } from '@svenskaspelab/poolbetting/schema/marketplace';
import { CouponAdditionsItem, CouponAdditionsWalletSelector } from '@svenskaspelab/poolbetting/ui/CouponAdditions';
import { useRouter, useSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';

import { getWalletsData, type InjectedWalletProps, withContextWallet } from '@/components/index.browser';

type PixWalletSelectorContainerClientProps = {
  className?: string;
} & InjectedWalletProps;

/**
 * PIX wallet selector that reads from wallet context and updates the activatedDrawId in the URL when a wallet is selected.
 * If no marketplace wallets are available, it renders nothing.
 *
 * @param root0 props
 * @param root0.className optional className for styling the container
 * @param root0.contextWallets wallets injected by withContextWallet HOC
 * @returns wallet selector UI or nothing if no marketplace wallets exist
 */
function PixWalletSelectorContainerClientInner({
  className,
  contextWallets,
}: PixWalletSelectorContainerClientProps): ReactNode {
  const router = useRouter();
  const searchParams = useSearchParams();
  const accountBalance = useAccountBalance();

  if (!contextWallets || contextWallets.length === 0) {
    return undefined;
  }

  const wallets = getWalletsData(accountBalance.balance, contextWallets);

  const activatedDrawIdQs = searchParams.get('activatedDrawId');
  const selectedWallet: WalletType | ActivatedDrawId | undefined = activatedDrawIdQs
    ? (Number.parseInt(activatedDrawIdQs, 10) as ActivatedDrawId)
    : undefined;

  const handleValueChange = (value: ActivatedDrawId | WalletType): void => {
    const qs = new URLSearchParams(searchParams.toString());
    if (value === 'WALLET_PLAYER') {
      qs.delete('activatedDrawId');
    } else {
      qs.set('activatedDrawId', `${value}`);
    }
    router.replace(`?${qs.toString()}`, { scroll: false });
  };

  return (
    <CouponAdditionsItem
      className={className}
      title="Välj spelkassa"
      description="Vill du spela med ditt eget spelkonto eller ett lags spelkonto?"
    >
      <CouponAdditionsWalletSelector
        wallets={wallets}
        onValueChange={handleValueChange}
        disabledWallets={[]}
        selectedCouponWallet={selectedWallet}
      />
    </CouponAdditionsItem>
  );
}

export default withContextWallet(PixWalletSelectorContainerClientInner);
